import { Inject, Injectable } from "@angular/core";
import { PaymentStatus } from "@bca-confirmation/models/_enums";
import { IStartPaymentResponse } from "@bca-confirmation/models/start-payment-response";
import { IStatusPaymentResponse } from "@bca-confirmation/models/status-payment-response";
import * as confirmationActions from "@bca-confirmation/store/confirmation.actions";
import { ConfirmationActionTypes } from "@bca-confirmation/store/confirmation.actions";
import { ApiService } from "@bca-core/services/api.service";
import { RouterService } from "@bca-core/services/router.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { MessageType, Route } from "@bca-shared/models/_enums";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { Currency, NsShop, PaymentType } from "@bca-shared/utils/avo/Avo";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { SalesChanelType } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectTotalPrice } from "@bca-store/store.selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { EMPTY } from "rxjs";
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class ConfirmationEffects {
  private avoPurchaseTriggered = false;

  navigateToAgreementOnFailure = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.SetPaymentStatus),
      filter(
        ({ payload }: confirmationActions.SetPaymentStatus) =>
          payload.status === PaymentStatus.Cancelled ||
          payload.status === PaymentStatus.Expired ||
          payload.status === PaymentStatus.Failure ||
          payload.status === PaymentStatus.Unknown,
      ),
      tap(() =>
        this._store$.dispatch(
          this._createAction(new receiptActions.SetCompletedStatus({ completed: false })),
        ),
      ),
      tap(() => this._routerService.navigateTo(Route.AGREEMENT)),
      map(() =>
        this._createAction(new appActions.SetRoute({ route: Route.AGREEMENT })),
      ),
    ),
  );

  redirectToIssuer = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.SetTransaction),
      filter(
        ({ payload }: confirmationActions.SetTransaction) => payload != null,
      ),
      filter(({ payload: transaction }: confirmationActions.SetTransaction) => {
        return transaction.paymentStatus === PaymentStatus.Start;
      }),
      tap((action: confirmationActions.SetTransaction) => {
        const { customerBankEntryPoint } = action.payload;
        this._routerService.redirectToUrl(customerBankEntryPoint);
        this._store$.dispatch(
          this._createAction(
            new confirmationActions.RedirectToIssuerLoading({ loading: true }),
          ),
        );
      }),
      map(() =>
        this._createAction(new appActions.SetRoute({ route: Route.ISSUER })),
      ),
    ),
  );

  startPayment = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.StartPayment),
      withLatestFrom(this._store$.pipe(select(({ receipt }) => receipt))),
      switchMap(([, receipt]) => {
        this._store$.dispatch(
          this._createAction(
            new confirmationActions.StartPaymentLoading({ loading: true }),
          ),
        );
        return this._apiService.startPayment(receipt).pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.START_PAYMENT",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message)),
            );
            return EMPTY;
          }),
        );
      }),
      filter((response: IStartPaymentResponse) => response.success),
      mergeMap(
        ({
          id,
          customerBankEntryPoint,
          referenceNumber,
        }: IStartPaymentResponse) => {
          return this._createActions([
            new confirmationActions.SetTransaction({
              id,
              customerBankEntryPoint,
              paymentStatus: PaymentStatus.Start,
              referenceNumber,
            }),
            new confirmationActions.StartPaymentLoading({ loading: false }),
          ]);
        },
      ),
    ),
  );

  statusPayment = createEffect(() =>
    this._actions.pipe(
      ofType(ConfirmationActionTypes.StatusPayment),
      withLatestFrom(
        this._store$.pipe(
          select(({ confirmation }) => confirmation.transaction),
        ),
        this._store$.pipe(select(({ receipt }) => receipt)),
        this._store$.pipe(select(selectTotalPrice)),
        this._store$.pipe(
          select(({ confirmation }) => confirmation.paymentFailed),
        ),
        this._store$.pipe(
          select(({ confirmation }) => confirmation.purchaseEventSent),
        ),
      ),
      filter(([, transaction]) => transaction != null),
      switchMap(
        ([
          ,
          transaction,
          receipt,
          totalPrice,
          hasPaymentFailed,
          purchaseEventSent,
        ]) => {
          this._store$.dispatch(
            this._createAction(
              new confirmationActions.StatusPaymentLoading({ loading: true }),
            ),
          );
          return this._apiService.statusPayment(transaction.id).pipe(
            catchError((error) => {
              const message = {
                body: "NOTIFICATION.ERROR.STATUS_PAYMENT",
                type: MessageType.ERROR,
                context: { error },
                originalException: error,
              };
              this._store$.dispatch(
                this._createAction(new appActions.SetMessage(message)),
              );
              return EMPTY;
            }),
            filter((response: IStatusPaymentResponse) => response.success),
            tap((response: IStatusPaymentResponse) => {
              if (
                response.paymentStatus === PaymentStatus.Success &&
                !hasPaymentFailed &&
                !purchaseEventSent
              ) {
                this._avoService.purchase({
                  currency: Currency.EUR,
                  transactionId: transaction?.id,
                  coupon: receipt?.actionCode?.code || null,
                  tax: null,
                  value: totalPrice / 100,
                  items: this._avoDataTransformService.transformItems(
                    receipt?.products || [],
                  ),
                  nsShop: NsShop.NS_BUSINESSCARD,
                  paymentType: PaymentType.IDEAL,
                  ticketClass: undefined,
                  salesChannel: SalesChanelType.BCA,
                  existingProduct: (receipt?.products || []).map((product) => ({
                    itemId: product.identifier,
                    itemName: product.identifier,
                    itemBrand: "BCA",
                    itemCategory: "Abonnementen",
                    itemCategory2: "Abonnementen",
                    itemVariant: "BCA",
                    price: product.price,
                    quantity: 1,
                  })),
                  discount: null,
                  couponType: null,
                });

                this._store$.dispatch(
                  this._createAction(
                    new confirmationActions.SetPurchaseEventSent({
                      avoCapiPurchaseEvent: true,
                    }),
                  ),
                );
              }
            }),
            mergeMap((response: IStatusPaymentResponse) => {
              const { paymentStatus, referenceNumber } = response;
              const actions: IAction[] = [
                new confirmationActions.SetReferenceNumber({
                  referenceNumber: referenceNumber,
                }),
                new confirmationActions.SetPaymentStatus({
                  status: paymentStatus,
                }),
                new confirmationActions.StatusPaymentLoading({
                  loading: false,
                }),
              ];
              if (paymentStatus === PaymentStatus.Success) {
                actions.push(new receiptActions.SetCompletedStatus({ completed: true }));
              }
              return this._createActions(actions);
            }),
          );
        },
      ),
    ),
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, ConfirmationEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, ConfirmationEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _routerService: RouterService,
    private _apiService: ApiService,
    private _avoService: AvoService,
    private _avoDataTransformService: AvoDataTransformService,
    @Inject(APP_CONFIG) private _config: IAppConfiguration,
  ) {}
}
